import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useScrollMemory } from './scrollMemory'
import {
  Mail, FileText, Paperclip, Lock, Unlock, Layers, List, Circle,
  Clock, Bolt, Zap, AlertTriangle, ArrowDown, ArrowUp, ChevronRight, ChevronDown, Pencil,
  Folder as FolderIcon, FolderPlus, Sparkles, Archive, RotateCcw,
} from 'lucide-react'
import { manualEdit, matters as mattersApi, type Artifact, type Matter } from '../api'
import EditableText from './EditableText'
import { FolderPickerModal, ConfirmActionModal } from './FolderSidebar'
import { useFlashOnChange } from './useFlashOnChange'

type NarrativeGroup = NonNullable<Matter['narrative_groups']>[number]

/**
 * Matter timeline pane — the middle column in the workspace when a
 * matter is selected. Mirrors the React prototype:
 *   v2_design_docs/Working Thinking Claude Design prototype/src/workspace.jsx
 *   (TimelinePane + StageGroupedTimeline + SpineTimeline + FeedTimeline)
 *
 * Three view modes:
 *   - Spine — vertical line, dot per artifact, chronological newest-first
 *   - Stages — grouped by per-matter-type stage templates, time-bucketed
 *   - Feed — flat list with chips inline (urgency, etc.)
 *
 * The user has a default style (stored on me_timeline_style); each
 * matter can lock its own override (stored as a map under
 * me_matter_style). Lock icon next to the segmented control.
 */

type TimelineStyle = 'spine' | 'stages' | 'feed'

const DEFAULT_STYLE_KEY = 'me_timeline_style'
const PER_MATTER_STYLE_KEY = 'me_matter_style'

function loadDefaultStyle(): TimelineStyle {
  try {
    const v = localStorage.getItem(DEFAULT_STYLE_KEY)
    if (v === 'spine' || v === 'stages' || v === 'feed') return v
  } catch { /* ignore */ }
  return 'stages'
}

function loadPerMatterStyles(): Record<number, TimelineStyle> {
  try {
    return JSON.parse(localStorage.getItem(PER_MATTER_STYLE_KEY) || '{}')
  } catch {
    return {}
  }
}

// ─── Stage templates per matter type ─────────────────────────────────────
//
// Days-ago span is half-open: artifact's `(now - source_date)` in days
// must be <= span[0] AND > span[1] (where -1 means "all the way to now").
// Lifted directly from the prototype's StageGroupedTimeline.

// Stage templates — mirrors apps/matters/stages.py STAGE_TEMPLATES.
// Keep in sync: every matter_type in the backend dict needs a matching
// entry here, and each stage's `id` must match exactly. Backend is the
// source of truth (the AI picks an id from it).
type StageDef = { id: string; label: string }

const STAGE_TEMPLATES: Record<string, StageDef[]> = {
  contract: [
    { id: 'intake',    label: '1 · Intake & scoping' },
    { id: 'diligence', label: '2 · Diligence' },
    { id: 'draft',     label: '3 · Draft & negotiate' },
    { id: 'signature', label: '4 · Signature & close' },
  ],
  litigation: [
    { id: 'pre_suit',  label: '1 · Pre-suit' },
    { id: 'pleadings', label: '2 · Pleadings' },
    { id: 'discovery', label: '3 · Discovery' },
    { id: 'active',    label: '4 · Active' },
  ],
  employment: [
    { id: 'intake',         label: '1 · Intake' },
    { id: 'investigation',  label: '2 · Investigation' },
    { id: 'resolution',     label: '3 · Resolution' },
  ],
  ip: [
    { id: 'evaluation',  label: '1 · Evaluation' },
    { id: 'prosecution', label: '2 · Prosecution' },
    { id: 'maintenance', label: '3 · Maintenance' },
    { id: 'enforcement', label: '4 · Enforcement' },
  ],
  ma: [
    { id: 'loi',        label: '1 · LOI / Term sheet' },
    { id: 'diligence',  label: '2 · Diligence' },
    { id: 'definitive', label: '3 · Definitive agreement' },
    { id: 'closing',    label: '4 · Closing' },
  ],
  real_estate: [
    { id: 'intake',    label: '1 · Intake' },
    { id: 'diligence', label: '2 · Diligence' },
    { id: 'closing',   label: '3 · Closing' },
  ],
  tax: [
    { id: 'planning',    label: '1 · Planning' },
    { id: 'compliance',  label: '2 · Compliance' },
    { id: 'controversy', label: '3 · Controversy' },
  ],
  corporate: [
    { id: 'formation',   label: '1 · Formation' },
    { id: 'governance',  label: '2 · Governance' },
    { id: 'transaction', label: '3 · Transactions' },
  ],
  other: [
    // Mirror of apps/matters/stages.py:STAGE_TEMPLATES['other'].
    // Mid-stages are status-flavored ("work in progress" / "awaiting
    // response") — that's deliberate for the catch-all bucket.
    { id: 'intake',            label: '1 · Intake' },
    { id: 'work_in_progress',  label: '2 · Work in progress' },
    { id: 'awaiting_response', label: '3 · Awaiting response' },
    { id: 'concluded',         label: '4 · Concluded' },
  ],
}

function templateForMatterType(matterType: string): StageDef[] {
  return STAGE_TEMPLATES[matterType] || STAGE_TEMPLATES.other
}

// ─── Top-level pane ──────────────────────────────────────────────────────

type Props = {
  matter: Matter
  artifacts: Artifact[]
  selectedArtifactId: number | null
  /** Pass `null` to clear the selection (closes the right detail pane). */
  onSelectArtifact: (id: number | null) => void
  /** Slot for the bulk-action toolbar from Workspace, rendered above
   *  the timeline area but below the matter header. */
  toolbar?: React.ReactNode
  /** sessionStorage key for restoring this pane's scroll position.
   *  Workspace passes `me.scroll.artifacts.<matterId>` so each matter
   *  remembers where the user was. */
  scrollKey?: string
  /** True while react-query is fetching a fresh artifacts list. With
   *  keepPreviousData enabled upstream, we keep the previous matter's
   *  list visible (rather than blanking) but dim it + show a loading
   *  hint so the user knows it isn't fresh yet. */
  isFetching?: boolean
}

export default function MatterTimelinePane({
  matter, artifacts, selectedArtifactId, onSelectArtifact, toolbar, scrollKey, isFetching,
}: Props) {
  const scrollRef = useRef<HTMLElement>(null)
  // Per-matter scroll restore on every key change — restoring "your place
  // in matter X" feels expected when bouncing between matters or coming
  // back later. (Workspace controls the global matters list separately
  // via the pending-flag mechanism.)
  useScrollMemory(scrollRef, scrollKey ?? `me.scroll.artifacts.${matter.id}`, 'always')
  const [defaultStyle, setDefaultStyle] = useState<TimelineStyle>(loadDefaultStyle)
  const [perMatterStyle, setPerMatterStyle] = useState<Record<number, TimelineStyle>>(loadPerMatterStyles)
  // Show-details toggle: hides subject + sender/date/id metadata when
  // off so each row is just the AI summary + badges. Default OFF —
  // browsing a matter is usually about scanning AI summaries; the
  // subject is rarely the most informative thing. Persisted to
  // localStorage so the preference sticks across reloads/matters.
  const [showDetails, setShowDetailsState] = useState<boolean>(() => {
    try { return (localStorage.getItem('me_artifact_show_details') ?? '0') === '1' }
    catch { return false }
  })
  const setShowDetails = (v: boolean) => {
    setShowDetailsState(v)
    try { localStorage.setItem('me_artifact_show_details', v ? '1' : '0') } catch { /* ignore */ }
  }
  const [sortDir, setSortDir] = useState<'desc' | 'asc'>(() => {
    try {
      const v = localStorage.getItem('me_timeline_sort_dir')
      return v === 'asc' ? 'asc' : 'desc'
    } catch {
      return 'desc'
    }
  })
  const toggleSortDir = () => {
    const next = sortDir === 'desc' ? 'asc' : 'desc'
    setSortDir(next)
    try { localStorage.setItem('me_timeline_sort_dir', next) } catch { /* ignore */ }
  }

  const lockedStyle = perMatterStyle[matter.id]
  const activeStyle: TimelineStyle = lockedStyle || defaultStyle
  const isLocked = !!lockedStyle

  const setStyle = (style: TimelineStyle, lock: boolean) => {
    if (lock) {
      const next = { ...perMatterStyle, [matter.id]: style }
      setPerMatterStyle(next)
      try { localStorage.setItem(PER_MATTER_STYLE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
    } else {
      const next = { ...perMatterStyle }
      delete next[matter.id]
      setPerMatterStyle(next)
      try { localStorage.setItem(PER_MATTER_STYLE_KEY, JSON.stringify(next)) } catch { /* ignore */ }
      // Also bump the user default if the click was a style change
      // while not locked (mirrors the prototype's behavior).
      if (style !== defaultStyle && !isLocked) {
        setDefaultStyle(style)
        try { localStorage.setItem(DEFAULT_STYLE_KEY, style) } catch { /* ignore */ }
      }
    }
  }

  const sorted = useMemo(
    () => [...artifacts].sort((a, b) => {
      const ad = a.source_date || ''
      const bd = b.source_date || ''
      return sortDir === 'desc' ? bd.localeCompare(ad) : ad.localeCompare(bd)
    }),
    [artifacts, sortDir],
  )

  return (
    <section ref={scrollRef} className="me-scroll flex h-full flex-col overflow-auto bg-[color:var(--me-surface)]">
      {/* Header — title + urgency only, so long matter names get the
          full column width instead of being squeezed by the segmented
          control. View-mode + sort controls live ABOVE the artifact
          list below. */}
      <MatterHeader matter={matter} />

      {/* Bulk-action toolbar (passed in from Workspace). Only rendered
          if Workspace actually passes one — currently it doesn't for
          the matter view, since bulk-run lives in Studio. */}
      {toolbar && <div className="px-3 pt-2">{toolbar}</div>}

      {/* View / sort controls, sitting just above the artifact list so
          they move with the content, not the matter brief. Inherits
          the pane's surface background; only a top border separates
          it from the matter brief above. */}
      <div className="flex items-center gap-1 border-t me-border px-3 py-1.5">
        <ViewModeControls
          style={activeStyle}
          isLocked={isLocked}
          onStyleChange={setStyle}
        />
        <label
          className="ml-2 inline-flex cursor-pointer items-center gap-1.5 text-[11px] me-text-mute select-none"
          title="Show subject, sender, date, and id on each row"
        >
          <input
            type="checkbox"
            checked={showDetails}
            onChange={(e) => setShowDetails(e.target.checked)}
            className="cursor-pointer"
          />
          details
        </label>
        <span className="ml-auto" />
        <SortToggle sortDir={sortDir} onToggle={toggleSortDir} />
      </div>

      {/* Timeline area. While the parent's artifacts query is loading
          (matter switch or first load with no cached data), the list is
          empty and a centered spinner takes over. Background refetches
          on already-loaded data don't fire this — only true empty
          states. */}
      <div
        className="relative flex-1 px-3 py-2"
        aria-busy={isFetching ? 'true' : undefined}
      >
        {isFetching && (
          <div
            className="pointer-events-none absolute inset-0 z-10 flex items-start justify-center pt-16"
            aria-label="Loading"
            role="status"
          >
            <div className="size-8 rounded-full border-[3px] border-[color:var(--me-text-mute)] border-t-transparent animate-spin" />
          </div>
        )}
        {sorted.length === 0 && !isFetching && (
          <div className="rounded-sm border border-dashed me-border p-3 text-[12px] me-text-mute">
            No artifacts on this matter yet.
          </div>
        )}
        {sorted.length > 0 && activeStyle === 'spine' && (
          <SpineTimeline
            artifacts={sorted}
            matter={matter}
            selectedId={selectedArtifactId}
            onSelect={onSelectArtifact}
            showDetails={showDetails}
            sortDir={sortDir}
          />
        )}
        {sorted.length > 0 && activeStyle === 'stages' && (
          <StagesTimeline
            artifacts={sorted}
            matter={matter}
            selectedId={selectedArtifactId}
            onSelect={onSelectArtifact}
            stagesAsc={sortDir === 'asc'}
            showDetails={showDetails}
          />
        )}
        {sorted.length > 0 && activeStyle === 'feed' && (
          <FeedTimeline
            artifacts={sorted}
            selectedId={selectedArtifactId}
            onSelect={onSelectArtifact}
            showDetails={showDetails}
          />
        )}
      </div>
    </section>
  )
}

// ─── Header ──────────────────────────────────────────────────────────────

function MatterHeader({ matter }: { matter: Matter }) {
  const code = matter.composite_id || `M-${matter.client_seq.toString().padStart(3, '0')}`
  // The matter list query is identified by ['matters', { limit: 500 }] across
  // Workspace + ArtifactViewer; invalidate that AND the per-matter detail.
  const inv: readonly (string | number | object)[][] = [
    ['matters', { limit: 500 }],
    ['matter', matter.id],
  ]
  // Flash on AI updates from background polls (rename, summary regen,
  // urgency bump, etc.). matter.id pinned as identityKey so navigating
  // to a different matter doesn't trigger a spurious flash — only true
  // value changes on the same matter fire. See useFlashOnChange.
  const nameFlash = useFlashOnChange(matter.name, matter.id)
  const summaryFlash = useFlashOnChange(matter.summary || '', matter.id)
  const waitingFlash = useFlashOnChange(matter.waiting_on || '', matter.id)
  const stageFlash = useFlashOnChange(matter.stage, matter.id)
  const urgencyFlash = useFlashOnChange(matter.urgency, matter.id)
  return (
    <div className="border-b me-border px-4 py-3">
      <div className="flex items-center gap-2">
        <h1 className={`text-[17px] font-semibold tracking-tight me-text leading-tight flex-1 min-w-0 break-words ${nameFlash ? 'me-flash' : ''}`}>
          <EditableText
            value={matter.name}
            target={{ type: 'matter', id: matter.id, field: 'name' }}
            mode="inline"
            maxLength={255}
            invalidateKeys={inv}
            copyOnClick
          />
        </h1>
        {matter.urgency >= 4 && (
          <span className={`me-chip me-chip-urgent flex items-center gap-1 shrink-0 ${urgencyFlash ? 'me-flash' : ''}`}>
            <Bolt size={9} /> urgency {matter.urgency}/5
          </span>
        )}
      </div>
      {/* Static metadata row (code + type + stage display). The stage value
          is editable below as its own labeled block — keeping it editable
          inline here would make a tiny dropdown squeeze between dots. */}
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] me-text-mute">
        <span className="me-mono uppercase">{code}</span>
        <span>·</span>
        <span className="me-mono uppercase">{matter.matter_type}</span>
        {matter.stage && <><span>·</span><span className={stageFlash ? 'me-flash' : ''}>{matter.stage.replace(/_/g, ' ')}</span></>}
      </div>
      {/* Waiting-on as its own block (was inline-flex in a chip → editing
          would shove the row sideways). Editor is now block-level so it
          gets the full column width and doesn't push siblings around. */}
      <div className="mt-2 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-[12px]">
        <span className="me-text-mute inline-flex items-center gap-1 pt-[2px]">
          <Clock size={11} /> waiting on
        </span>
        <div className={`me-text-dim ${waitingFlash ? 'me-flash' : ''}`}>
          <EditableText
            value={matter.waiting_on || ''}
            target={{ type: 'matter', id: matter.id, field: 'waiting_on' }}
            mode="block"
            multiline
            maxLength={255}
            invalidateKeys={inv}
            placeholder="—"
            copyOnClick
          />
        </div>
        <span className="me-text-mute pt-[2px]">stage</span>
        <div className={`me-text-dim ${stageFlash ? 'me-flash' : ''}`}>
          <MatterStageEditor matter={matter} invalidateKeys={inv} />
        </div>
      </div>
      <div className={`mt-2 max-w-[68ch] text-[12.5px] leading-snug me-text-dim ${summaryFlash ? 'me-flash' : ''}`}>
        <EditableText
          value={matter.summary || ''}
          target={{ type: 'matter', id: matter.id, field: 'summary' }}
          mode="block"
          multiline
          maxLength={5000}
          invalidateKeys={inv}
          placeholder="(no summary yet)"
          copyOnClick
        />
      </div>
      <MatterFolderChips matter={matter} />
      {matter.keywords?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {matter.keywords.slice(0, 7).map((k) => (
            <span key={k} className="me-chip text-[10px]">{k}</span>
          ))}
        </div>
      )}
    </div>
  )
}

/** Inline chips of folder assignments + a small "Manage" button that
 *  opens the picker for moving / multi-filing this matter. Phase B
 *  minimum-viable surface — adds the capability without touching the
 *  Workspace sidebar. */
function MatterFolderChips({ matter }: { matter: Matter }) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [archiveOpen, setArchiveOpen] = useState(false)
  const [restoreOpen, setRestoreOpen] = useState(false)
  const qc = useQueryClient()
  const assignments = matter.folder_assignments || []
  const currentFolderIds = new Set(assignments.map((a) => a.folder))
  const archiveMutation = useMutation({
    mutationFn: () => mattersApi.update(matter.id, { status: 'closed' } as Partial<Matter>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matters'] })
      qc.invalidateQueries({ queryKey: ['matter', matter.id] })
      // Folder counts depend on matter status (closed matters excluded).
      qc.invalidateQueries({ queryKey: ['folders', 'tree'] })
    },
  })
  const restoreMutation = useMutation({
    mutationFn: () => mattersApi.update(matter.id, { status: 'intake' } as Partial<Matter>),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['matters'] })
      qc.invalidateQueries({ queryKey: ['matter', matter.id] })
      qc.invalidateQueries({ queryKey: ['folders', 'tree'] })
    },
  })
  const isArchived = matter.status === 'closed'
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5 text-[11px] relative">
      <FolderIcon size={11} className="me-text-mute" />
      {assignments.length === 0 && (
        <span className="me-text-mute italic">unfiled</span>
      )}
      {assignments.map((a) => (
        <span
          key={a.id}
          className="me-chip inline-flex items-center gap-1"
          title={`Filed ${a.assigned_by === 'ai' ? 'by AI' : 'by user'} on ${new Date(a.assigned_at).toLocaleDateString()}`}
        >
          {a.assigned_by === 'ai' && <Sparkles size={9} className="me-text-mute" />}
          <span>{a.folder_name}</span>
        </span>
      ))}
      <button
        onClick={() => setPickerOpen((v) => !v)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5 inline-flex items-center gap-1"
        title="Manage folders for this matter"
      >
        <FolderPlus size={10} /> manage
      </button>
      {!isArchived ? (
        <button
          onClick={() => setArchiveOpen(true)}
          disabled={archiveMutation.isPending}
          className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5 inline-flex items-center gap-1 disabled:opacity-50"
          title="Archive this matter"
        >
          <Archive size={10} /> archive
        </button>
      ) : (
        <button
          onClick={() => setRestoreOpen(true)}
          disabled={restoreMutation.isPending}
          className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-0.5 inline-flex items-center gap-1 disabled:opacity-50"
          title="Restore this matter from archive"
        >
          <RotateCcw size={10} /> restore
        </button>
      )}
      {pickerOpen && (
        <FolderPickerModal
          matter={matter}
          currentFolderIds={currentFolderIds}
          onClose={() => setPickerOpen(false)}
        />
      )}
      {archiveOpen && (
        <ConfirmActionModal
          title={`Archive "${matter.name}"?`}
          body={<>It moves to the <span className="me-mono">Archived</span> section and stays accessible. You can restore it any time.</>}
          confirmLabel="Archive"
          pending={archiveMutation.isPending}
          onCancel={() => setArchiveOpen(false)}
          onConfirm={() => { archiveMutation.mutate(); setArchiveOpen(false) }}
        />
      )}
      {restoreOpen && (
        <ConfirmActionModal
          title={`Restore "${matter.name}"?`}
          body={<>It moves back to the active matters list with its prior state intact.</>}
          confirmLabel="Restore"
          pending={restoreMutation.isPending}
          onCancel={() => setRestoreOpen(false)}
          onConfirm={() => { restoreMutation.mutate(); setRestoreOpen(false) }}
        />
      )}
    </div>
  )
}

/** Stage is constrained to the matter_type's template, so a free-text
 *  EditableText is wrong — render a select dropdown. Saves via the
 *  same /api/manual-edit/ endpoint. */
function MatterStageEditor({
  matter, invalidateKeys,
}: { matter: Matter; invalidateKeys: readonly (string | number | object)[][] }) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(matter.stage || '')
  const qc = useQueryClient()
  const stages = templateForMatterType(matter.matter_type)
  const save = useMutation({
    mutationFn: () => manualEdit({
      target_type: 'matter', target_id: matter.id, field: 'stage', value: draft,
    }),
    onSuccess: () => {
      setEditing(false)
      for (const k of invalidateKeys) qc.invalidateQueries({ queryKey: k })
    },
  })
  if (!editing) {
    return (
      <span className="group inline-flex items-center gap-1.5">
        <span>{(matter.stage || '').replace(/_/g, ' ') || <span className="me-text-mute italic">(unset)</span>}</span>
        <button
          onClick={() => { setDraft(matter.stage || ''); setEditing(true) }}
          className="me-text-mute opacity-0 group-hover:opacity-50 hover:opacity-100 hover:me-text"
          title="Edit stage"
          type="button"
        >
          <Pencil size={11} />
        </button>
      </span>
    )
  }
  return (
    <div className="space-y-1.5">
      <select
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        className="me-input w-full text-[13px]"
      >
        <option value="">(unset)</option>
        {stages.map((s) => (
          <option key={s.id} value={s.id}>{s.label}</option>
        ))}
      </select>
      <div className="flex items-center gap-1">
        <button
          onClick={() => save.mutate()}
          disabled={save.isPending || draft === (matter.stage || '')}
          className="me-btn me-btn-primary !text-[11px] !py-1"
          type="button"
        >{save.isPending ? 'Saving…' : 'Save'}</button>
        <button
          onClick={() => setEditing(false)}
          className="me-btn me-btn-outline !text-[11px] !py-1"
          type="button"
        >Cancel</button>
      </div>
      {save.error && (
        <div className="text-[11px] text-[color:var(--me-stale)]">{(save.error as Error).message}</div>
      )}
    </div>
  )
}

function ViewModeControls({
  style, isLocked, onStyleChange,
}: {
  style: TimelineStyle
  isLocked: boolean
  onStyleChange: (s: TimelineStyle, lock: boolean) => void
}) {
  const styleOpts: Array<{ id: TimelineStyle; label: string; icon: React.ReactNode }> = [
    { id: 'spine',  label: 'Spine',  icon: <Circle size={10} /> },
    { id: 'stages', label: 'Stages', icon: <Layers size={10} /> },
    { id: 'feed',   label: 'Feed',   icon: <List size={10} /> },
  ]
  return (
    <>
      <div className="inline-flex overflow-hidden rounded-sm border me-border">
        {styleOpts.map((o) => {
          const active = style === o.id
          return (
            <button
              key={o.id}
              onClick={() => onStyleChange(o.id, isLocked)}
              data-selected={active}
              className="me-btn !rounded-none !text-[10px] !px-2 !py-1 inline-flex items-center gap-1"
              style={active ? {
                background: 'var(--me-selected)',
                color: 'var(--me-text)',
                fontWeight: 600,
              } : {}}
              title={`Timeline: ${o.label}`}
            >
              {o.icon}
              {o.label}
            </button>
          )
        })}
      </div>
      <button
        onClick={() => onStyleChange(style, !isLocked)}
        className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-1 inline-flex items-center gap-1 ml-1"
        title={isLocked ? 'Unlock — fall back to your default style' : 'Lock this style for this matter'}
        style={isLocked ? {
          background: 'var(--me-selected)',
          color: 'var(--me-text)',
        } : {}}
      >
        {isLocked ? <Lock size={10} /> : <Unlock size={10} />}
        {isLocked ? 'locked' : 'lock'}
      </button>
    </>
  )
}

function SortToggle({
  sortDir, onToggle,
}: {
  sortDir: 'desc' | 'asc'
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      className="me-btn me-btn-outline !text-[10px] !px-1.5 !py-1 inline-flex items-center gap-1"
      title={sortDir === 'desc' ? 'Newest first (click for oldest first)' : 'Oldest first (click for newest first)'}
    >
      {sortDir === 'desc' ? <ArrowDown size={10} /> : <ArrowUp size={10} />}
      {sortDir === 'desc' ? 'newest' : 'oldest'}
    </button>
  )
}

// ─── Narrative-group bucketing ────────────────────────────────────────────
//
// narrate_matter_history emits a list of NarrativeGroups: themed phases of
// the matter, each owning a set of artifact_ids. Both the spine and stages
// views use these as the top-level units (instead of flat artifact lists),
// so the matter reads as a story rather than a stream.
//
// Properties used downstream:
//   - earliest:     min source_date across the group's artifacts (sort key)
//   - latest:       max source_date (display)
//   - artifacts:    resolved Artifact[] from the group's items[]
//   - dominantStage: the stage_id that the most artifacts in the group
//                    sit in (used to bucket groups under stages)
//   - notesByAid:   {artifact_id: note} for the per-row note, surfaced
//                    inline in the expanded view

type ResolvedGroup = {
  label: string
  summary: string
  artifacts: Artifact[]
  earliest: string  // ISO source_date or ''
  latest: string
  dominantStage: string  // stage_id or ''
  notesByAid: Record<number, string>
  /** Index of each artifact within group.items[] — used to build the
   *  manual-edit field path for the per-item note. Undefined for orphans. */
  itemIndexByAid: Record<number, number>
  /** Original index of the group as the AI returned it. The prompt asks
   *  for chronological order across groups, so this preserves that
   *  authorial order even when artifact-date sort would shuffle. */
  origIndex: number
}

function resolveGroups(
  groups: NarrativeGroup[] | undefined,
  artifacts: Artifact[],
): ResolvedGroup[] {
  if (!groups || groups.length === 0) return []
  const byId = new Map(artifacts.map((a) => [a.id, a]))
  return groups.map((g, idx) => {
    const items = Array.isArray(g.items) ? g.items : []
    const resolved: Artifact[] = []
    const notes: Record<number, string> = {}
    const itemIndexByAid: Record<number, number> = {}
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      const a = byId.get(it.artifact_id)
      if (a) {
        resolved.push(a)
        if (it.note) notes[it.artifact_id] = it.note
        itemIndexByAid[it.artifact_id] = i
      }
    }
    const dates = resolved.map((a) => a.source_date || '').filter(Boolean).sort()
    const stageCounts = new Map<string, number>()
    for (const a of resolved) {
      const s = ((a as any).stage as string) || ''
      stageCounts.set(s, (stageCounts.get(s) || 0) + 1)
    }
    let dominantStage = ''
    let max = 0
    for (const [s, n] of stageCounts) {
      if (s && n > max) { dominantStage = s; max = n }
    }
    return {
      label: g.label || '(unlabeled)',
      summary: g.summary || '',
      artifacts: resolved,
      earliest: dates[0] || '',
      latest: dates[dates.length - 1] || '',
      dominantStage,
      notesByAid: notes,
      itemIndexByAid,
      origIndex: idx,
    }
  }).filter((g) => g.artifacts.length > 0)
}

// Find artifacts that aren't referenced by any narrative group — they
// should still be visible (so users can see "this artifact came in but
// the AI hasn't grouped it yet"). Bucketed into a synthetic "Unfiled"
// pseudo-group for both views.
function unfiledGroup(
  groups: NarrativeGroup[] | undefined,
  artifacts: Artifact[],
): ResolvedGroup | null {
  if (!groups || groups.length === 0) return null
  const seen = new Set<number>()
  for (const g of groups) {
    for (const it of g.items || []) seen.add(it.artifact_id)
  }
  const orphans = artifacts.filter((a) => !seen.has(a.id))
  if (orphans.length === 0) return null
  const dates = orphans.map((a) => a.source_date || '').filter(Boolean).sort()
  return {
    label: 'Unfiled',
    summary: 'Artifacts the latest narrate run did not place into a phase.',
    artifacts: orphans,
    earliest: dates[0] || '',
    latest: dates[dates.length - 1] || '',
    dominantStage: '',
    notesByAid: {},
    itemIndexByAid: {},
    origIndex: 9999,  // sort to end
  }
}

// ─── Timeline view: Spine ─────────────────────────────────────────────────

function SpineTimeline({
  artifacts, matter, selectedId, onSelect, showDetails, sortDir,
}: {
  artifacts: Artifact[]
  matter: Matter
  selectedId: number | null
  onSelect: (id: number | null) => void
  showDetails?: boolean
  sortDir: 'desc' | 'asc'
}) {
  const groups = useMemo(() => {
    const resolved = resolveGroups(matter.narrative_groups, artifacts)
    const unfiled = unfiledGroup(matter.narrative_groups, artifacts)
    if (unfiled) resolved.push(unfiled)
    // Authorial order from the AI when ascending; reversed for newest-first.
    return sortDir === 'asc' ? resolved : [...resolved].reverse()
  }, [matter.narrative_groups, artifacts, sortDir])

  // No groups → fall back to flat artifact spine (e.g. matter has no
  // narrate run yet, or all artifacts are unfiled).
  if (groups.length === 0) {
    return (
      <div className="relative pl-6">
        <div
          className="absolute top-2 bottom-2 w-px"
          style={{ left: 8, background: 'var(--me-border)' }}
        />
        {artifacts.map((a) => (
          <TimelineArtifactRow
            key={a.id}
            artifact={a}
            selected={selectedId === a.id}
            onSelect={() => onSelect(a.id)}
            showSpine
            showDetails={showDetails}
          />
        ))}
      </div>
    )
  }

  return (
    <div className="relative pl-6">
      <div
        className="absolute top-2 bottom-2 w-px"
        style={{ left: 8, background: 'var(--me-border)' }}
      />
      {groups.map((g) => (
        <NarrativeGroupRow
          key={`spine-${g.origIndex}-${g.label}`}
          group={g}
          matterId={matter.id}
          selectedId={selectedId}
          onSelect={onSelect}
          showSpine
          showDetails={showDetails}
          artifactsSortDir={sortDir}
        />
      ))}
    </div>
  )
}

// ─── Narrative-group row (collapsed by default; expand to see artifacts) ──

function NarrativeGroupRow({
  group, matterId, selectedId, onSelect, showSpine, showDetails, artifactsSortDir,
}: {
  group: ResolvedGroup
  matterId: number
  selectedId: number | null
  onSelect: (id: number | null) => void
  showSpine?: boolean
  showDetails?: boolean
  artifactsSortDir: 'desc' | 'asc'
}) {
  // Auto-expand if this group contains the selected artifact (so a
  // selection from search / chat lands on a visible row).
  const containsSelected = selectedId != null
    && group.artifacts.some((a) => a.id === selectedId)
  const [open, setOpen] = useState<boolean>(containsSelected)
  useEffect(() => {
    if (containsSelected) setOpen(true)
  }, [containsSelected])
  const toggleOpen = () => {
    setOpen((wasOpen) => {
      const willClose = wasOpen
      // Collapsing a group that contained the user's selected artifact
      // also clears the selection — feels like the artifact "went away
      // with the group" rather than staying highlighted in a now-hidden
      // row, and matches the empty right-pane state of fresh matter entry.
      if (willClose && containsSelected) onSelect(null)
      return !wasOpen
    })
  }

  const sorted = useMemo(() => {
    return [...group.artifacts].sort((a, b) => {
      const ad = a.source_date || ''
      const bd = b.source_date || ''
      return artifactsSortDir === 'desc' ? bd.localeCompare(ad) : ad.localeCompare(bd)
    })
  }, [group.artifacts, artifactsSortDir])

  // Two-level structure (so the expanded artifacts area is a SIBLING
  // of the flex row, NOT a child — earlier shape made the expanded
  // area a flex child that stole all available width and collapsed
  // the label/summary column to 0px, causing character-by-character
  // wrap):
  //   <wrapper-div>
  //     <header-row role=button display=flex>  (toggle target)
  //       chevron, label/summary stack
  //     </header-row>
  //     {open && <artifacts-area>}             (sibling, full width)
  //   </wrapper-div>
  return (
    <div className="mb-1.5">
      <div
        onClick={toggleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
            e.preventDefault(); toggleOpen()
          }
        }}
        className="me-row w-full text-left relative cursor-pointer"
        style={{ padding: '8px 10px', display: 'flex', alignItems: 'flex-start', gap: 8 }}
      >
        {showSpine && (
          <div
            className="absolute"
            style={{
              left: -19,
              top: 14,
              width: 10, height: 10,
              borderRadius: 5,
              background: 'var(--me-surface)',
              border: '1.5px solid var(--me-borderStrong, var(--me-border))',
            }}
          />
        )}
        {open
          ? <ChevronDown size={13} className="me-text-mute mt-0.5 shrink-0" />
          : <ChevronRight size={13} className="me-text-mute mt-0.5 shrink-0" />
        }
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="font-semibold me-text truncate flex-1 min-w-0 text-[12.5px]">
              <EditableText
                value={group.label}
                target={{ type: 'matter', id: matterId, field: `narrative_groups[${group.origIndex}].label` }}
                mode="inline"
                maxLength={80}
                invalidateKeys={[['matters', { limit: 500 }], ['matter', matterId]]}
                placeholder="(unlabeled)"
              />
            </span>
            <span className="me-mono text-[10.5px] me-text-mute shrink-0">
              {group.artifacts.length} artifact{group.artifacts.length === 1 ? '' : 's'}
            </span>
            {group.earliest && (
              <span className="me-mono text-[10.5px] me-text-mute shrink-0">
                {fmtDateShort(group.earliest)}
                {group.latest && group.latest !== group.earliest && ` → ${fmtDateShort(group.latest)}`}
              </span>
            )}
          </div>
          <div
            className="mt-1 text-[11.5px] leading-snug me-text-dim"
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            <EditableText
              value={group.summary || ''}
              target={{ type: 'matter', id: matterId, field: `narrative_groups[${group.origIndex}].summary` }}
              mode="block"
              multiline
              maxLength={600}
              invalidateKeys={[['matters', { limit: 500 }], ['matter', matterId]]}
              placeholder="(no summary)"
            />
          </div>
        </div>
      </div>{/* header-row close */}
      {open && (
        <div className="ml-5 mt-1 pl-3 border-l me-border">
          {sorted.map((a) => {
            const note = group.notesByAid[a.id]
            // Find this artifact's index within group.items[] so we can
            // build the dotted-path field for the per-item-note edit.
            const itemIndex = group.itemIndexByAid[a.id]
            return (
              <TimelineArtifactRow
                key={a.id}
                artifact={a}
                matterId={matterId}
                selected={selectedId === a.id}
                onSelect={() => onSelect(a.id)}
                showDetails={showDetails}
                groupNote={note}
                noteEditTarget={
                  itemIndex != null
                    ? { groupIndex: group.origIndex, itemIndex }
                    : undefined
                }
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Timeline view: Stages ────────────────────────────────────────────────

function StagesTimeline({
  artifacts, matter, selectedId, onSelect, stagesAsc, showDetails,
}: {
  artifacts: Artifact[]
  matter: Matter
  selectedId: number | null
  onSelect: (id: number | null) => void
  stagesAsc: boolean
  showDetails?: boolean
}) {
  // Templates are defined oldest-first (intake → close). When the user
  // wants newest-first overall, present the latest stage at top by
  // reversing the stage order.
  const baseStages = templateForMatterType(matter.matter_type)
  const stages = stagesAsc ? baseStages : [...baseStages].reverse()
  const earliestStageId = baseStages[0]?.id

  // When narrative_groups exist, this view shows narrative groups bucketed
  // under each stage (group → dominant stage of its artifacts). When they
  // don't, fall back to the original artifact-level bucketing.
  const groups = useMemo(
    () => resolveGroups(matter.narrative_groups, artifacts),
    [matter.narrative_groups, artifacts],
  )
  const unfiled = useMemo(
    () => unfiledGroup(matter.narrative_groups, artifacts),
    [matter.narrative_groups, artifacts],
  )
  const hasGroups = groups.length > 0

  return (
    <div>
      <div className="mb-2 me-mono text-[10px] me-text-mute tracking-wider uppercase">
        Stages · {matter.matter_type}
      </div>
      {stages.map((st) => {
        if (hasGroups) {
          // Bucket narrative groups by their dominant stage. Groups with
          // no dominant stage fall into the earliest stage (same default
          // as orphan artifacts).
          const groupsInStage = groups.filter((g) => {
            const stage = g.dominantStage || earliestStageId || ''
            return stage === st.id
          })
          return (
            <div key={st.id} className="mb-4">
              <div className="mb-1.5 flex items-center gap-2 text-[10.5px] font-semibold tracking-wider uppercase me-text-mute">
                <span>{st.label}</span>
                <div className="flex-1 h-px" style={{ background: 'var(--me-border)' }} />
                <span className="me-mono">{groupsInStage.length} group{groupsInStage.length === 1 ? '' : 's'}</span>
              </div>
              {groupsInStage.length === 0 ? (
                <div className="px-2 py-1 text-[11px] italic me-text-mute">
                  No groups in this stage.
                </div>
              ) : (
                groupsInStage.map((g) => (
                  <NarrativeGroupRow
                    key={`stage-${st.id}-${g.origIndex}-${g.label}`}
                    group={g}
                    matterId={matter.id}
                    selectedId={selectedId}
                    onSelect={onSelect}
                    showDetails={showDetails}
                    artifactsSortDir={stagesAsc ? 'asc' : 'desc'}
                  />
                ))
              )}
            </div>
          )
        }

        // No narrative groups → flat artifact bucketing (legacy behavior).
        const inStage = artifacts.filter((a) => {
          const stage = (a as any).stage as string | undefined
          if (stage) return stage === st.id
          return st.id === earliestStageId
        })
        return (
          <div key={st.id} className="mb-4">
            <div className="mb-1.5 flex items-center gap-2 text-[10.5px] font-semibold tracking-wider uppercase me-text-mute">
              <span>{st.label}</span>
              <div className="flex-1 h-px" style={{ background: 'var(--me-border)' }} />
              <span className="me-mono">{inStage.length}</span>
            </div>
            {inStage.length === 0 ? (
              <div className="px-2 py-1 text-[11px] italic me-text-mute">
                No artifacts in this stage.
              </div>
            ) : (
              inStage.map((a) => (
                <TimelineArtifactRow
                  key={a.id}
                  artifact={a}
                  selected={selectedId === a.id}
                  onSelect={() => onSelect(a.id)}
                  showDetails={showDetails}
                />
              ))
            )}
          </div>
        )
      })}
      {hasGroups && unfiled && (
        <div className="mb-4">
          <div className="mb-1.5 flex items-center gap-2 text-[10.5px] font-semibold tracking-wider uppercase me-text-mute">
            <span>Unfiled</span>
            <div className="flex-1 h-px" style={{ background: 'var(--me-border)' }} />
            <span className="me-mono">1 group</span>
          </div>
          <NarrativeGroupRow
            group={unfiled}
            matterId={matter.id}
            selectedId={selectedId}
            onSelect={onSelect}
            showDetails={showDetails}
            artifactsSortDir={stagesAsc ? 'asc' : 'desc'}
          />
        </div>
      )}
    </div>
  )
}

// ─── Timeline view: Feed ──────────────────────────────────────────────────

function FeedTimeline({
  artifacts, selectedId, onSelect, showDetails,
}: {
  artifacts: Artifact[]
  selectedId: number | null
  onSelect: (id: number | null) => void
  showDetails?: boolean
}) {
  return (
    <div>
      {artifacts.map((a) => (
        <TimelineArtifactRow
          key={a.id}
          artifact={a}
          selected={selectedId === a.id}
          onSelect={() => onSelect(a.id)}
          showChips
          showDetails={showDetails}
        />
      ))}
    </div>
  )
}

// ─── Artifact row used by all three views ────────────────────────────────

// Cap on the rendered summary length. Today's MOVU max is 692 chars
// (avg 277). 1500 is comfortably above any current summary AND keeps a
// single pathological row from exploding the column. If we ever
// generate longer summaries, this bound + an ellipsis is the failure
// mode — a click into the artifact still shows the full text.
const SUMMARY_MAX_CHARS = 1500

function TimelineArtifactRow({
  artifact, matterId, selected, onSelect, showSpine, showChips, showDetails, groupNote, noteEditTarget,
}: {
  artifact: Artifact
  /** Matter id powers the per-item-note + (future per-artifact) edits. */
  matterId?: number
  selected: boolean
  onSelect: () => void
  showSpine?: boolean
  showChips?: boolean
  /** When true, render the subject + sender/date/id metadata above the
   *  summary. When false (the default in matter views), the row is just
   *  the AI summary + badges. */
  showDetails?: boolean
  /** When this row appears inside a NarrativeGroupRow, the group's
   *  per-artifact `note` field is shown above the AI summary. */
  groupNote?: string
  /** When set (only inside narrative groups), the groupNote becomes
   *  editable via the manual-edit endpoint. */
  noteEditTarget?: { groupIndex: number; itemIndex: number }
}) {
  const code = artifact.composite_id || `#${artifact.client_seq}`
  const fromName = artifact.from_name || artifact.from_email || ''
  const isUrgent = (artifact.urgency ?? 0) >= 4
  const Icon = artifact.artifact_type === 'email' ? Mail
            : artifact.artifact_type === 'attachment' ? Paperclip
            : FileText
  const summary = artifact.ai_summary || ''
  const summaryDisplay = summary.length > SUMMARY_MAX_CHARS
    ? summary.slice(0, SUMMARY_MAX_CHARS) + '…'
    : summary
  // Restructured from the original full-row <button>: use a div with a
  // small role="button" outer wrapper so EditableText (textarea) doesn't
  // sit inside a <button> and bubble keystrokes (spacebar) into a click.
  // Selection still happens on click anywhere outside the editable
  // children — we stop propagation INSIDE EditableText.
  return (
    <div
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          // Mirror the implicit-button-keypress behavior, but only when
          // focus is on the row itself, not a nested input.
          if (e.target === e.currentTarget) {
            e.preventDefault()
            onSelect()
          }
        }
      }}
      data-selected={selected ? 'true' : undefined}
      className="me-row mb-0.5 w-full gap-2.5 text-left relative cursor-pointer"
      style={{ padding: '8px 10px', display: 'flex' }}
    >
      {showSpine && (
        <div
          className="absolute"
          style={{
            left: -19,
            top: 14,
            width: 10, height: 10,
            borderRadius: 5,
            background: 'var(--me-surface)',
            border: '1.5px solid var(--me-borderStrong, var(--me-border))',
          }}
        />
      )}
      <Icon size={13} className="me-text-mute mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        {/* Subject + bibliographic metadata only when `details` is on.
         *  When details is off the row is just the AI summary (clean
         *  reading view); the subject lives in the third-column
         *  artifact viewer where it's always editable. */}
        {showDetails && (
          <>
            <div className="flex items-baseline gap-2 min-w-0">
              <span className="font-medium me-text flex-1 min-w-0 text-[12.5px]">
                <EditableText
                  value={artifact.subject || ''}
                  target={{ type: 'artifact', id: artifact.id, field: 'subject' }}
                  mode="inline"
                  maxLength={998}
                  invalidateKeys={[
                    ['artifact', artifact.id],
                    ['artifacts', 'matter', matterId ?? -1],
                    ['artifactsPage'],
                  ]}
                  placeholder="(no subject)"
                />
              </span>
              {artifact.source_date && (
                <span className="me-mono text-[10.5px] me-text-mute shrink-0">
                  {fmtRelative(artifact.source_date)}
                </span>
              )}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px] me-text-mute">
              <span className="me-mono">{code}</span>
              {fromName && <><span>·</span><span className="truncate">{fromName}</span></>}
              {artifact.parent_artifact && <Paperclip size={9} className="me-text-mute" />}
            </div>
          </>
        )}
        {/* Group note — editable when we're inside a narrative group AND
            we know the matter id + item index. Otherwise plain display. */}
        {noteEditTarget && matterId != null ? (
          <div
            className={`text-[11px] italic me-text-mute ${showDetails ? 'mt-1' : ''}`}
            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            <EditableText
              value={groupNote || ''}
              target={{
                type: 'matter', id: matterId,
                field: `narrative_groups[${noteEditTarget.groupIndex}].items[${noteEditTarget.itemIndex}].note`,
              }}
              mode="block"
              maxLength={240}
              invalidateKeys={[['matters', { limit: 500 }], ['matter', matterId]]}
              placeholder="(no note)"
            />
          </div>
        ) : (
          groupNote && (
            <div
              className={`text-[11px] italic me-text-mute ${showDetails ? 'mt-1' : ''}`}
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {groupNote}
            </div>
          )
        )}
        {/* AI summary — the primary content of the row. Editable inline
            so users can correct AI summaries without opening the artifact
            viewer. (Editor click bubbles are stopped by EditableText.) */}
        <div
          className={`text-[11.5px] leading-snug me-text-dim ${(showDetails || groupNote) ? 'mt-1' : ''}`}
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          <EditableText
            value={summaryDisplay}
            target={{ type: 'artifact', id: artifact.id, field: 'ai_summary' }}
            mode="block"
            multiline
            maxLength={4000}
            invalidateKeys={[
              ['artifact', artifact.id],
              // The matter list query backs the timeline view's artifact data;
              // refresh both possible cache buckets after an edit.
              ['artifacts', 'matter', matterId ?? -1],
              ['artifactsPage'],
            ]}
            placeholder="(no AI summary — run pipeline to generate)"
          />
        </div>
        {(showChips || isUrgent || artifact.is_sensitive) && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {isUrgent && (
              <span className="me-chip me-chip-urgent inline-flex items-center gap-1 text-[10px]">
                <Zap size={9} /> urgent
              </span>
            )}
            {artifact.is_sensitive && (
              <span className="me-chip me-chip-stale inline-flex items-center gap-1 text-[10px]">
                <AlertTriangle size={9} /> sensitive
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function fmtRelative(iso: string): string {
  const d = new Date(iso)
  const ms = Date.now() - d.getTime()
  const day = 86400000
  if (ms < day) {
    const h = Math.floor(ms / 3600000)
    if (h <= 0) return 'just now'
    if (h === 1) return '1h ago'
    return `${h}h ago`
  }
  const days = Math.floor(ms / day)
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

function fmtDateShort(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const m = d.toLocaleString('en-US', { month: 'short' })
  return `${m} ${d.getDate()}`
}
